import api from "lib/api";
import { useCallback } from "react";
import produce from "immer";
import { State } from "hooks/usePagedData";
import { ApiNotificationProps } from "components/Notifications/Notifications";
import useUnreadCounts from "./useUnreadCount";

export function useNotifications(
	state: State,
	patchState: (data: State) => void
) {
	const handleUnreadCount = useUnreadCounts();

	/**
	 * Handles toggling read status of a notification
	 *
	 * @param id notification
	 * @param index in state.results
	 */
	const handleRead = useCallback(
		async (id, index) => {
			await api.notifications.read(id);

			patchState(
				produce(state, draft => {
					draft.results[index].read = !draft.results[index].read;
				})
			);
			await handleUnreadCount();
		},
		[patchState, state, handleUnreadCount]
	);

	const handleAllRead = useCallback(async () => {
		await api.notifications.markAllRead();

		patchState(
			produce(state, draft => {
				for (let j = 0; j < draft.results.length; j++) {
					draft.results[j].read = true;
				}
			})
		);

		await handleUnreadCount();
	}, [patchState, state, handleUnreadCount]);

	const handleDelete = useCallback(
		async (id: string) => {
			await api.notifications.deleteOne(id);

			patchState(
				produce(state, draft => {
					const index = draft.results.findIndex(
						(notif: ApiNotificationProps) => {
							return notif._id === id;
						}
					);
					draft.results.splice(index, 1);
					const newOffset = draft.offset - 1;
					draft.offset = newOffset;
				})
			);

			await handleUnreadCount();
		},
		[patchState, state, handleUnreadCount]
	);

	const handleDeleteAll = useCallback(async () => {
		await api.notifications.deleteAll();
		patchState(
			produce(state, draft => {
				draft.results.splice(0, draft.results.length);
			})
		);
		await handleUnreadCount();
	}, [handleUnreadCount, patchState, state]);

	return { handleAllRead, handleDelete, handleDeleteAll, handleRead };
}
